import api from './api';

/**
 * Push Notifications Service
 * Handles service worker registration and push notification setup
 */

export const pushNotificationsService = {
  /**
   * Initialize push notifications
   * Registers service worker and requests notification permission
   */
  async init() {
    try {
      // Check if service workers are supported
      if (!('serviceWorker' in navigator)) {
        return false;
      }

      // Register the service worker
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      // Check for service worker updates immediately on init
      try {
        registration.update();
      } catch (e) {}

      // Listen for updates to the service worker and show toast to let user refresh
      if (registration && registration.waiting) {
        // Dispatch event for App to show update toast
        window.dispatchEvent(
          new CustomEvent('sw:update-available', {
            detail: { registration },
          }),
        );
      }
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New update available, dispatch event for App to show toast
              window.dispatchEvent(
                new CustomEvent('sw:update-available', {
                  detail: { registration },
                }),
              );
            }
          });
        }
      });

      // Request notification permission
      const permission = await this.requestPermission();

      if (permission === 'granted') {
        // Subscribe to push notifications
        await this.subscribe();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      return false;
    }
  },

  /**
   * Request user permission for notifications
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  },

  /**
   * Subscribe to push notifications
   */
  async subscribe() {
    try {
      const registration = await navigator.serviceWorker.ready;

      if (!registration.pushManager) {
        return null;
      }

      // Check if already subscribed
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        // Get VAPID public key from server
        const vapidResponse = await api.get('/notifications/vapid-public-key');
        const vapidPublicKeyRaw = vapidResponse.data?.vapidPublicKey ?? null;
        const vapidPublicKey =
          typeof vapidPublicKeyRaw === 'string' ? vapidPublicKeyRaw.trim() : null;
        const pushConfigured = Boolean(vapidResponse.data?.configured) || Boolean(vapidPublicKey);

        if (!pushConfigured || !vapidPublicKey || vapidPublicKey.length === 0) {
          return null;
        }

        // Subscribe to push notifications
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
        });


        // Send subscription to server
        await api.post('/notifications/subscribe', {
          subscription: subscription.toJSON(),
        });

      }

      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return null;
    }
  },

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        // Notify server of unsubscription
        await api.post('/notifications/unsubscribe', {
          subscription: subscription.toJSON(),
        });

      }
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
    }
  },

  /**
   * Convert VAPID public key from base64 to Uint8Array
   */
  urlBase64ToUint8Array(base64String: string | null | undefined): Uint8Array {
    if (
      base64String == null ||
      typeof base64String !== 'string' ||
      base64String.trim().length === 0
    ) {
      return new Uint8Array();
    }
    const normalized = base64String.trim();
    const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
    const base64 = (normalized + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  },

  /**
   * Get current push notification status
   */
  async getStatus() {
    try {
      if (!('serviceWorker' in navigator) || !('Notification' in window)) {
        return {
          supported: false,
          permission: 'denied',
          subscribed: false,
        };
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager?.getSubscription?.();

      return {
        supported: true,
        permission: Notification.permission,
        subscribed: !!subscription,
      };
    } catch (error) {
      console.error('Error getting push notification status:', error);
      return {
        supported: false,
        permission: 'denied',
        subscribed: false,
      };
    }
  },
};

export default pushNotificationsService;
